import { ArrowLeft, Question, SkipForward, Sparkle } from "@phosphor-icons/react";
import { ArrowRight, ChevronDown, HelpCircle, Loader2 } from "lucide-react";
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { GatewayDiagnosis } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { useAssistant } from "./assistant-context";
import { canBeFixedOnSite } from "./connector-issue";
import { ESCALATE_PROMISE, ESCALATE_PROMISE_PAUSED } from "./escalation";
import { useFlowNav } from "./flow-nav";

/**
 * Shared onboarding UI primitives, mirrored from the Figma component library
 * (page "🧩 Components"): pill CTA, top app bar, phase dots, help hint.
 */

interface PillButtonProps {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  busy?: boolean;
  busyLabel?: string;
  variant?: "primary" | "secondary" | "ghost";
  withArrow?: boolean;
  type?: "button" | "submit";
  className?: string;
}

/** Pill-shaped CTA — Figma "Button / primary" (60px tall, radius 30). */
export function PillButton({
  label,
  onClick,
  disabled,
  busy,
  busyLabel = "Saving…",
  variant = "primary",
  withArrow = true,
  type = "button",
  className,
}: PillButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      className={cn(
        /* min-h, not a fixed h: a long label (or any translation) wraps to a
           second line, and a fixed 60px clipped the wrap — the arrow ended up
           on a half-visible second row. One-line buttons still render at
           exactly 60px; two-line ones grow and keep the arrow beside the
           text block. */
        "flex min-h-[60px] w-full items-center justify-center gap-2 rounded-md px-5 py-2.5 text-center text-[17px] transition-colors disabled:opacity-60",
        /* Primary = brand Yellow with black type (Nico's verdict after the
           red and black trials); secondary = black stroke only. */
        variant === "primary" && "bg-daylight text-primary active:bg-daylight-pressed",
        variant === "secondary" &&
          "border-primary text-primary active:bg-primary/5 border bg-transparent",
        variant === "ghost" && "text-muted h-auto py-2",
        className,
      )}
    >
      {busy ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          {busyLabel}
        </>
      ) : (
        <>
          {label}
          {/* Primary only. The forward arrow is what marks the one CTA that
              moves the flow on; giving it to the secondary variant too makes
              the pair read as two competing primaries, and the extra 28px of
              gap+glyph pushes longer secondary labels onto a second line at
              375pt. */}
          {withArrow && variant === "primary" && <ArrowRight className="h-5 w-5 shrink-0" />}
        </>
      )}
    </button>
  );
}

interface AppBarProps {
  title?: string;
  /**
   * Step back within this screen's own stages. Omit it and back leaves the
   * module for the guide (see `flow-nav`), which is what a module's first
   * stage wants.
   */
  onBack?: () => void;
  /**
   * Which flow step/stage this screen is, so the `?` can hand the assistant
   * enough to gather live state. Omit `stepKey` to hide the help button.
   */
  stepKey?: string | null;
  stage?: string | null;
}

/**
 * Top app bar — Figma 1131:919. Bare 24px light glyphs in 30px slots (no
 * circle chips), a Body/Large/18 title, and a transparent background so it
 * reads as part of the page. Same chrome as the guide/home screen.
 *
 * There is deliberately no eyebrow above the title. Figma has one, but in
 * practice it held a constant repeated across every stage of a module
 * ("Phase 4" on twelve screens) — progress the dots already show — at a size
 * and tracking that was hard to read. The bar answers "where am I" with the
 * module name; each screen's own heading says what to do.
 */
export function AppBar({ title, onBack, stepKey, stage }: AppBarProps) {
  const assistant = useAssistant();
  const nav = useFlowNav();
  // A screen's own stage-back wins; otherwise back exits to the guide. Neither
  // available means there is genuinely nowhere to go, so no button is drawn.
  const goBack = onBack ?? nav?.exitToGuide;
  // Each stage is a new page of instructions: start it at the top. Keyed on
  // stage/stepKey so in-stage re-renders (polls, checklist ticks) never yank
  // the scroll position.
  //
  // Layout effect, not effect: this runs before the browser paints, so a stage
  // swap never shows one frame of the new screen at the old scroll offset.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [stepKey, stage]);
  return (
    <div className="flex h-14 w-full items-center px-[18px] py-1.5">
      {goBack ? (
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          className="flex size-[30px] shrink-0 items-center justify-center"
        >
          <ArrowLeft weight="light" className="text-muted size-6" />
        </button>
      ) : (
        <div className="size-[30px] shrink-0" />
      )}
      <div className="flex min-w-0 flex-1 items-center justify-center">
        {title && <p className="text-primary type-body-lg">{title}</p>}
      </div>
      {stepKey ? (
        <button
          type="button"
          onClick={() => assistant.open({ stepKey, stage })}
          aria-label="Get help with this step"
          className="flex size-[30px] shrink-0 items-center justify-center"
        >
          <Question weight="light" className="text-muted size-6" />
        </button>
      ) : (
        <div className="size-[30px] shrink-0" />
      )}
    </div>
  );
}

interface HelpHintProps {
  question: string;
  answer: string;
  /** When false the hint is always fully shown, with no chevron (Figma has both variants). */
  collapsible?: boolean;
}

/**
 * Troubleshooting hint — Figma "Help-Hint". Collapsible by default: collapsed
 * it shows just the bold question, tapping reveals the answer inline.
 */
export function HelpHint({ question, answer, collapsible = true }: HelpHintProps) {
  const [open, setOpen] = useState(false);

  const body = (
    <>
      <HelpCircle className="text-primary mt-px size-4 shrink-0" />
      <span className="text-primary min-w-0 flex-1 text-[13px] leading-[18px]">
        <span className="">{question}</span>
        {(open || !collapsible) && <span className="font-normal"> {answer}</span>}
      </span>
    </>
  );

  if (!collapsible) {
    return (
      <div className="bg-surface-subtle flex w-full items-start gap-2 rounded-xl px-3 py-2.5">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen((prev) => !prev)}
      aria-expanded={open}
      className="bg-surface-subtle flex w-full items-start gap-2 rounded-xl px-3 py-2.5 text-left"
    >
      {body}
      <ChevronDown
        className={cn(
          "text-primary mt-px size-4 shrink-0 transition-transform",
          open && "rotate-180",
        )}
      />
    </button>
  );
}

interface PhaseDotsProps {
  total: number;
  activeIndex: number;
}

/**
 * Phase indicator — Figma "step-indicator" (1131:929, 863:674): 6px dots with
 * an 8px gap, the active one stretched to a 24×6 orange pill and completed ones
 * filled dark.
 *
 * Flush against the app bar above it, as the design has it (the header is one
 * 62px block: 56px bar + 6px dots). Only the 8px below is ours, so a screen's
 * own top padding doesn't have to account for the indicator.
 */
export function PhaseDots({ total, activeIndex }: PhaseDotsProps) {
  return (
    <div
      role="progressbar"
      aria-label="Setup phase"
      aria-valuemin={0}
      aria-valuenow={activeIndex}
      aria-valuemax={total - 1}
      className="flex w-full items-center justify-center gap-2 pb-2"
    >
      {Array.from({ length: total }, (_, index) => (
        <div
          key={index}
          className={cn(
            "h-1.5 rounded-full transition-all",
            index === activeIndex
              ? "bg-accent w-6"
              : index < activeIndex
                ? "bg-primary w-1.5"
                : "bg-border w-1.5",
          )}
        />
      ))}
    </div>
  );
}

/** One item in a "what you'll need" list: a name, a caption, and a photo. */
export interface Material {
  title: string;
  /** Omitted when the part needs no explanation (Figma 1064:865, "Multi-outlet"). */
  caption?: string;
  /** Path under /brand/onboarding for the product/device photo, if we have one. */
  img?: string;
  /** Optional second photo (e.g. the Heating Connector's antenna). */
  img2?: string;
  alt?: string;
  /**
   * Exact photo box from the design, in px. Figma sizes each part's crop
   * individually (a cable reads differently to a switch), so when a screen
   * knows its boxes it passes them instead of taking the shared default.
   */
  imgWidth?: number;
  imgHeight?: number;
}

/**
 * "WHAT YOU'LL NEED" materials list — the shared intro block every module
 * uses to show the kit parts (with photos) before its setup steps. Mirrors
 * Figma's "Frame / what you'll need" (Eyebrow/10 label + surface-canvas
 * cards). Keeping it here means every module's intro renders identically.
 */
export function MaterialsNeeded({ items }: { items: Material[] }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted type-eyebrow">WHAT YOU&apos;LL NEED</p>
      {items.map((item) => (
        <div
          key={item.title}
          className="bg-background flex items-start gap-3 rounded-2xl px-4 py-3"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-primary text-[17px] leading-6">{item.title}</p>
            {item.caption && <p className="text-muted type-body-sm">{item.caption}</p>}
          </div>
          {item.img && (
            <img
              src={item.img}
              alt={item.alt ?? ""}
              style={
                item.imgWidth && item.imgHeight
                  ? { width: item.imgWidth, height: item.imgHeight }
                  : undefined
              }
              className={cn(
                "shrink-0 object-contain",
                !(item.imgWidth && item.imgHeight) && "h-16 w-auto max-w-[104px]",
              )}
            />
          )}
          {item.img2 && (
            <img
              src={item.img2}
              alt=""
              className="h-14 w-auto max-w-[64px] shrink-0 object-contain"
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * "Still retrying" or "genuinely failed", for a poll whose failing state
 * should not be trusted the instant it appears.
 *
 * A reachability poll (the hub-status check is the first user) reports a
 * failure on the way to success: the backend answers "not reachable yet" for
 * a poll cycle or two before the hub comes up, and a screen that renders that
 * immediately makes a normal boot look broken (VLX-78).
 * `useRetryGate` turns the raw "is it failing right now" signal into a
 * verdict that only flips to `"failed"` once the failure has lasted the
 * whole `windowMs`, so every screen deriving this waits the same way instead
 * of inventing its own threshold. Leaving the failing state (the caller's
 * `isFailing` going back to `false`) resets the gate to `"retrying"`
 * immediately, so re-entering a failing stretch later starts a fresh window
 * rather than inheriting time already spent.
 */
export type RetryGateState = "retrying" | "failed";

export function useRetryGate(isFailing: boolean, windowMs: number): RetryGateState {
  const [state, setState] = useState<RetryGateState>("retrying");
  const failingSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isFailing) {
      failingSinceRef.current = null;
      setState("retrying");
      return;
    }
    const since = failingSinceRef.current ?? Date.now();
    failingSinceRef.current = since;
    const elapsed = Date.now() - since;
    if (elapsed >= windowMs) {
      setState("failed");
      return;
    }
    const timer = setTimeout(() => setState("failed"), windowMs - elapsed);
    return () => clearTimeout(timer);
  }, [isFailing, windowMs]);

  return state;
}

/**
 * Tidy a backend status detail for display.
 *
 * These come through as `cannot reach <url>: <exception>`, and when the
 * exception carries no message (a bare ConnectError) we'd render a dangling
 * "…/api/config:". Render them with `break-words` too — the URL is a single
 * token with no break opportunities, so without it the text runs off-screen.
 */
export function formatStatusDetail(detail: string | null | undefined): string | null {
  if (!detail) {
    return null;
  }
  const trimmed = detail.trim().replace(/[\s:]+$/, "");
  return trimmed || null;
}

/** How long a doing-screen may sit idle before offering the assistant. */
export const STUCK_HELP_MS = 45_000;

interface StuckHelpProps {
  /** The question to pre-fill and send, phrased as the user would ask it. */
  seed: string;
  stepKey: string;
  stage?: string | null;
  /** The error text the screen is showing, so the assistant sees it too. */
  lastError?: string | null;
  /** See OpenOptions.carryOn: the screen's own button, named for the resident. */
  carryOn?: string;
  label?: string;
  /**
   * Hold the button back for this long. Used on screens that are *expected* to
   * wait (the hub boot poll): offering help in the first seconds reads as
   * "this is broken" when it isn't.
   */
  afterMs?: number;
  /**
   * Optional second phase: once the wait has outrun the expectation the screen
   * itself states, swap to a firmer label (and seed). The early label can then
   * stay gentle — an invitation, not an alarm — because it no longer has to
   * cover the genuinely-stuck case too. Both escalate props must be set
   * together; requires `escalateAfterMs > afterMs` to mean anything.
   */
  escalateAfterMs?: number;
  escalateLabel?: string;
  /** Seed for the escalated phase; falls back to `seed` when omitted. */
  escalateSeed?: string;
  /**
   * A stronger signal than elapsed time: the screen has confirmed an actual
   * failure, not just an ordinary wait. Shows the button right away, skipping
   * both `afterMs` and the escalate timer, and latches once true so a
   * transient recovery doesn't hide it again.
   *
   * Deliberately generic. What counts as "genuinely failed" versus "still
   * inside a normal retry window" is decided per screen, closest to the poll
   * that owns that distinction, not here.
   */
  hasFailed?: boolean;
}

/**
 * The stuck-state entry into the assistant, for the handful of screens where we
 * know people get stuck. Unlike the app bar's `?` it sends the problem straight
 * away, because a stuck user shouldn't have to describe what the app can
 * already see on their hub.
 *
 * Two triggers, not one (U7): ordinary elapsed time (`afterMs`), for the
 * person who is simply waiting longer than feels comfortable, and a confirmed
 * failure (`hasFailed`), for the person the app already knows is stuck. The
 * failure trigger skips the wait entirely, because a screen that already
 * knows something is wrong has no reason to make the resident sit out a timer
 * calibrated for the ordinary case first.
 */
export function StuckHelp({
  seed,
  stepKey,
  stage,
  lastError,
  carryOn,
  label = "Still stuck? Ask for help",
  afterMs = 0,
  escalateAfterMs,
  escalateLabel,
  escalateSeed,
  hasFailed = false,
}: StuckHelpProps) {
  const assistant = useAssistant();
  const [shown, setShown] = useState(afterMs === 0 || hasFailed);
  const [escalated, setEscalated] = useState(hasFailed);

  useEffect(() => {
    if (afterMs === 0) {
      return;
    }
    const timer = setTimeout(() => setShown(true), afterMs);
    return () => clearTimeout(timer);
  }, [afterMs]);

  // A confirmed failure outranks both timers, not just the idle one: it jumps
  // straight past the gentle phase to whatever the escalated label says,
  // because a screen that already knows something is wrong has nothing left
  // to be gentle about. Latches rather than toggling back off, so a
  // transient recovery (the next poll succeeding) doesn't pull the offer
  // away from someone who already saw it and started reading.
  useEffect(() => {
    if (hasFailed) {
      setShown(true);
      setEscalated(true);
    }
  }, [hasFailed]);

  useEffect(() => {
    // Already escalated by a confirmed failure; the timer below would only
    // set the same value again once it fires.
    if (hasFailed || !escalateAfterMs || !escalateLabel) {
      return;
    }
    // Enforce the documented contract instead of trusting call sites: an
    // escalation due at or before `afterMs` would skip the gentle phase, so an
    // invalid value gets no escalation at all. The gentle label is the designed
    // experience; a misconfigured call site degrades to gentle-only rather
    // than to alarm-at-first-sight.
    if (escalateAfterMs <= afterMs) {
      logger.warn(
        "[onboarding] StuckHelp escalateAfterMs must exceed afterMs; escalation disabled",
        {
          afterMs,
          escalateAfterMs,
        },
      );
      return;
    }
    const timer = setTimeout(() => setEscalated(true), escalateAfterMs);
    return () => clearTimeout(timer);
  }, [afterMs, escalateAfterMs, escalateLabel, hasFailed]);

  if (!shown) {
    return null;
  }

  const activeLabel = escalated && escalateLabel ? escalateLabel : label;
  const activeSeed = escalated ? (escalateSeed ?? seed) : seed;

  return (
    <button
      type="button"
      onClick={() => assistant.open({ stepKey, stage, seed: activeSeed, lastError, carryOn })}
      className="border-border hover:border-accent text-primary flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-3 text-[15px] leading-[22px] transition-colors"
    >
      <Sparkle weight="light" className="text-accent size-4" />
      {activeLabel}
    </button>
  );
}

interface SkipFailedStepProps {
  /** Records the skip and returns to the guide. Binds its own reason. */
  onSkip: () => void;
  busy?: boolean;
  label?: string;
  /**
   * Hold the button back this long, same as `StuckHelp`. Used on the connecting
   * stages, which do not fail so much as never finish: provisioning a USR
   * gateway legitimately runs to fifteen minutes, so an escape hatch offered in
   * the first seconds would read as "this is broken" when it isn't.
   */
  afterMs?: number;
  /**
   * Render nothing, but keep counting. For a screen that wants the button held
   * back for a reason of its own (an escalate notice already offering the way
   * on) without losing the patience timer: unmounting the component would
   * restart `afterMs` from zero the moment the reason went away, so a verdict
   * that flips back mid-wait would make the resident sit out the whole wait
   * again.
   */
  hidden?: boolean;
}

/**
 * The escape hatch from a compartment that cannot succeed.
 *
 * Some failures are not the resident's to fix: an integration missing from the
 * hub, a gateway that never provisions, a unit whose bus returns nothing. Every
 * later compartment sits behind the failing one, so without this the whole
 * install dead-ends on hardware nobody in the room can reach.
 *
 * Deliberately quiet, and deliberately only rendered next to an error: it is
 * the second thing to try, never the first. The copy promises a return trip
 * because the guide really does reopen a skipped compartment, and the closing
 * recap leaves it off the "what you set up" list rather than claiming credit.
 */
export function SkipFailedStep({
  onSkip,
  busy = false,
  label = "Skip this compartment for now",
  afterMs = 0,
  hidden = false,
}: SkipFailedStepProps) {
  const [shown, setShown] = useState(afterMs === 0);

  useEffect(() => {
    if (afterMs === 0) {
      return;
    }
    const timer = setTimeout(() => setShown(true), afterMs);
    return () => clearTimeout(timer);
  }, [afterMs]);

  if (!shown || hidden) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <button
        type="button"
        onClick={onSkip}
        disabled={busy}
        className="border-border hover:border-accent text-primary flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-3 text-[15px] leading-[22px] transition-colors disabled:opacity-60"
      >
        <SkipForward weight="light" className="text-accent size-4" />
        {label}
      </button>
      <p className="text-muted text-center text-[13px] leading-[18px]">
        You can come back to it from the guide once it is sorted.
      </p>
    </div>
  );
}

interface NeedsVeluxNoticeProps {
  /** What went wrong, in the app's own words. Omitted when there is nothing to
   *  add that the resident has not already read. */
  detail?: string;
  /**
   * Nothing runs until this one is fixed, so the promise must not end on
   * "carry on with the rest of the setup". The hub's connecting stage sets it:
   * every later module is provisioned through the hub, so there is no rest to
   * carry on with and no CTA offering it.
   */
  paused?: boolean;
  /** The ways on: a skip CTA, a question worth answering. */
  children?: ReactNode;
}

/**
 * A problem that is not the resident's to fix, wherever we find one.
 *
 * Shared by the connector screens' `DiagnosisNotice`, the help chat's
 * escalation card and the hub's connecting stage, so the promise is worded in
 * one place (`escalation.ts`) and looks the same whether it followed a hub
 * diagnosis, a conversation or a hub that never came up. The heading is the
 * point: the resident has just been told the app cannot help, and the next
 * thing they read has to be who will.
 *
 * `paused` picks which of the two promises is made. They differ only in the
 * last sentence, and that sentence is the whole difference between a
 * compartment (carry on with the rest) and a prerequisite (there is no rest
 * to carry on with).
 */
export function NeedsVeluxNotice({ detail, paused = false, children }: NeedsVeluxNoticeProps) {
  return (
    <div className="border-border bg-background flex w-full flex-col gap-3 rounded-2xl border p-4">
      <p className="text-primary text-[15px] leading-[22px] font-medium">
        VELUX support will sort this out
      </p>
      {detail && <p className="text-muted type-body-sm">{detail}</p>}
      <p className="text-muted type-body-sm">
        {paused ? ESCALATE_PROMISE_PAUSED : ESCALATE_PROMISE}
      </p>
      {children}
    </div>
  );
}

/**
 * Pinned under the chat once a reply carried a correction for VELUX: the
 * resident is not stuck, nothing at home is broken, and this names the one
 * thing to do next on the screen they came from.
 */
export function CarryOnNotice({ carryOn }: { carryOn: string }) {
  return (
    <div className="border-border bg-background flex w-full flex-col gap-2 rounded-2xl border p-4">
      <p className="text-primary text-[15px] leading-[22px] font-medium">
        Noted for VELUX. Nothing to fix at home.
      </p>
      <p className="text-muted type-body-sm">{carryOn}</p>
    </div>
  );
}

interface DiagnosisNoticeProps {
  /** A diagnosis that has already been confirmed a fault (see `isLinkFault`). */
  diagnosis: GatewayDiagnosis;
  stepKey: string;
  stage?: string | null;
  /** The connector this verdict is about, for the assistant seed's wording. */
  connectorName: string;
  /**
   * Records the issue and carries the household past this compartment, for the
   * escalate variant's CTA. Omit it and the notice explains without offering a
   * way on, which is what a screen with no skip of its own wants.
   */
  onContinue?: () => void;
  /** True while that skip is in flight, so the CTA can't be double-tapped. */
  busy?: boolean;
}

/**
 * The background diagnosis's verdict, shown the moment it lands.
 *
 * It used to arrive as a muted caption under the spinner, which put the one
 * sentence explaining why nothing was happening in the quietest type on the
 * screen, below a checklist still cheerfully ticking. The verdict is the most
 * useful thing the screen knows, so it gets a block of its own and it gets it
 * immediately: waiting out a twenty-second window before admitting to a fault
 * we have already established only spends the resident's patience.
 *
 * Two variants, split by `canBeFixedOnSite` rather than by a list of verdicts,
 * so a new verdict lands on the right side by construction and the connecting
 * stage's `isEscalation` cannot disagree with what this renders:
 *
 * - Someone on site CAN fix it: state the check and hand them to the assistant,
 *   which walks the fix. Retries keep running behind it, so a fix applied in
 *   the cabinet is picked up without anyone tapping anything.
 * - Nobody in the house can: say so plainly, say what we will do about it, and
 *   offer the one useful action left, which is to get on with the rest. No
 *   invitation to troubleshoot here on purpose. Sending someone after a fault
 *   we have just told them is not theirs wastes their time and undercuts the
 *   promise in the same breath as making it.
 *
 * An escalation can still carry a question, and that is not a contradiction:
 * the Wi-Fi `not_found` verdict is ours to sort out, and the one thing that
 * would speed it up is the resident reading a network name off their phone.
 * So it is asked as a favour to VELUX, in the chat where they can answer in
 * words, and never as the way out of the screen.
 */
export function DiagnosisNotice({
  diagnosis,
  stepKey,
  stage,
  connectorName,
  onContinue,
  busy = false,
}: DiagnosisNoticeProps) {
  if (canBeFixedOnSite(diagnosis)) {
    // The backend writes an on-site question for exactly these verdicts, and it
    // is sharper than the detail (it names the socket, the cable colour or the
    // access point). Detail is the fallback for a verdict that has none.
    const question = diagnosis.question ?? diagnosis.detail;
    return (
      <div className="bg-warning/10 flex w-full flex-col gap-3 rounded-2xl p-4">
        <p className="text-primary text-[15px] leading-[22px] font-medium">Something to check</p>
        <p className="text-muted type-body-sm">{question}</p>
        <StuckHelp
          stepKey={stepKey}
          stage={stage}
          // No timer. The screen has a verdict in hand, so there is nothing
          // left to wait and see about.
          afterMs={0}
          hasFailed
          lastError={diagnosis.detail}
          // The question is quoted, not folded into the sentence: this seed is
          // posted as the resident's own message in the transcript, and the
          // backend writes the question in the second person ("stand next to
          // the connector…"). Unquoted, the resident appears to be issuing
          // instructions to the assistant instead of relaying ours.
          seed={`The app asked me to check something on my ${connectorName}: "${question}" Can you guide me through it?`}
          label="Guide me through it"
        />
      </div>
    );
  }

  return (
    <NeedsVeluxNotice detail={diagnosis.detail}>
      {diagnosis.question && (
        <>
          <p className="text-muted type-body-sm">One answer helps VELUX with this.</p>
          {/* Quiet, and below the promise: the resident has just been told this
              is not theirs to fix, so the ask has to read as a favour rather
              than as another job. It goes to the chat because the answer is
              words (a network name, four characters off a phone screen) that no
              button on this screen could collect. */}
          <StuckHelp
            stepKey={stepKey}
            stage={stage}
            afterMs={0}
            hasFailed
            lastError={diagnosis.detail}
            seed={`The app asked me: "${diagnosis.question}"`}
            label="Answer in the chat"
          />
        </>
      )}
      {onContinue && <PillButton label="Continue with the rest" busy={busy} onClick={onContinue} />}
    </NeedsVeluxNotice>
  );
}

/**
 * Numbered instruction list — Figma "Step-List" (1150:1028, 1154:1086): a 28px
 * subtle chip holding the number, a 16px gutter, and 20px between steps. The
 * hub screens both use it, so the numbering reads identically across them.
 */
export function StepList({ steps }: { steps: string[] }) {
  return (
    <div className="flex w-full flex-col gap-5">
      {steps.map((step, index) => (
        <div key={index} className="flex w-full items-start gap-4">
          <div className="bg-surface-subtle flex size-7 shrink-0 items-center justify-center rounded-[14px]">
            <span className="text-primary text-[17px] leading-6">{index + 1}</span>
          </div>
          <p className="text-primary flex-1 text-[17px] leading-6">{step}</p>
        </div>
      ))}
    </div>
  );
}
